import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { DemoDescription, ExampleTag, OfferDemoHandle, ReplayButton } from './OfferDemoShared';

/** Each beat of the missed-call-to-booking thread, and when it lands relative to the start. */
const STEPS = [
  { at: 60 },
  { at: 280 },
  { at: 540 },
  { at: 800 },
  { at: 1020 },
] as const;
const LAST_STEP = STEPS.length;

/**
 * A preview of where the "Calls" offer is heading (labelled coming
 * soon on the page): a missed call gets a text back, the reply gets read,
 * and the booking lands on the calendar. Texting is not switched on yet. Plays once, in view, then holds on the booked chip.
 */
const OfferDemoCalls = forwardRef<OfferDemoHandle, object>((_props, ref) => {
  const reduced = useReducedMotion();
  const { ref: viewRef, inView } = useInView<HTMLDivElement>(0.4);
  const started = useRef(false);
  const [step, setStep] = useState(reduced ? LAST_STEP : 0);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };

  const play = useCallback(() => {
    if (reduced) return;
    clearTimers();
    setStep(0);
    STEPS.forEach((s, i) => {
      timers.current.push(window.setTimeout(() => setStep(i + 1), s.at));
    });
  }, [reduced]);

  useImperativeHandle(ref, () => ({ replay: play }));

  useEffect(() => {
    if (reduced || started.current || !inView) return;
    started.current = true;
    play();
  }, [reduced, inView, play]);

  useEffect(() => () => clearTimers(), []);

  const ended = step >= LAST_STEP;

  return (
    <div className="offerdemo offerdemo--calls" ref={viewRef}>
      <DemoDescription>
        Preview of a feature that is coming soon: a missed call at 11:40pm gets a text back in 41
        seconds, the customer replies asking for Thursday afternoon, and it is booked for Thursday
        2:15. Texting is not switched on yet.
      </DemoDescription>
      <ExampleTag label="Preview · coming soon" />
      {/* Every row is in the DOM and reserves its space from the first
          frame; only opacity toggles as `step` advances. Nothing ever
          moves an already-painted row (a naive "append and let flex
          push everything up" version measurably shifted the sysrow and
          bubble on every step - real, if small, layout shift) so this
          demo contributes zero Cumulative Layout Shift. */}
      <div className="offerdemo__thread" aria-hidden="true">
        <p className={`offerdemo__sysrow${step >= 1 ? ' is-shown' : ''}`}>Missed call · 11:40pm</p>
        <p className={`offerdemo__bubble offerdemo__bubble--out${step >= 2 ? ' is-shown' : ''}`}>
          Sorry we missed you: want to book a time this week?
          <span className="offerdemo__stamp">41s later</span>
        </p>
        <div className="offerdemo__replyslot">
          <p className={`offerdemo__typing${step === 3 ? ' is-shown' : ''}`}>
            <i />
            <i />
            <i />
          </p>
          <p className={`offerdemo__bubble offerdemo__bubble--in${step >= 4 ? ' is-shown' : ''}`}>
            thursday afternoon?
          </p>
        </div>
        <span className={`offerdemo__chip${step >= 5 ? ' is-shown' : ''}`}>Booked Thu 2:15</span>
      </div>
      <ReplayButton what="calls" visible={!reduced && ended} onClick={play} />
    </div>
  );
});

OfferDemoCalls.displayName = 'OfferDemoCalls';

export default React.memo(OfferDemoCalls);
